import { Component, signal, inject, OnInit, OnDestroy, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Header } from "./shared/header/header";
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { NavbarComponent } from './shared/navbar/navbar';
import { BreadcrumbComponent } from './shared/breadcrumb/breadcrumb';
import { AuthService } from './services/auth.service';
import { PermissionService } from './services/permission.service';
import { ScreenshotService } from './services/screenshot.service';
import { AppErrorService } from './services/app-error.service';
import { LandingService } from './services/landing.service';
import { interval, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, Header, MatIconModule, MatButtonModule, MatTooltipModule, NavbarComponent, BreadcrumbComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('caps-ui');
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private screenshotService = inject(ScreenshotService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private appErrorService = inject(AppErrorService);
  private landingService = inject(LandingService);

  // App-level error state (e.g. 401 not-provisioned)
  readonly hasApiError = this.appErrorService.hasApiError;

  // State management - use AuthService as single source of truth
  readonly isAuthenticated = toSignal(this.authService.isAuthenticated$, { initialValue: false });
  readonly showLoginPage = computed(() => {
    const authenticated = this.isAuthenticated();
    console.log('🔍 showLoginPage computed - isAuthenticated:', authenticated, 'showLoginPage:', !authenticated);
    return !authenticated;
  });
  readonly currentRoute = signal('/');
  readonly showDashboardButton = computed(() => {
    const authenticated = this.isAuthenticated();
    const onRoot = this.currentRoute() === '/';
    const showButton = authenticated && onRoot;
    console.log('🔍 showDashboardButton computed - authenticated:', authenticated, 'onRoot:', onRoot, 'currentRoute:', this.currentRoute(), 'showButton:', showButton);
    return showButton;
  });
  readonly isOnRootRoute = computed(() => this.router.url === '/');
  
  // Token monitoring
  private warningTimeoutId?: number;
  private expiryTimeoutId?: number;
  private warningSnackBarRef?: MatSnackBarRef<any>;
  private expiredSnackBarRef?: MatSnackBarRef<any>;
  
  // Tracks whether user was ever authenticated in this session.
  // Prevents showing "session expired" snackbar on fresh page load (no prior session).
  private hasBeenAuthenticated = false;

  // Router subscription
  private routerSubscription?: Subscription;

  async ngOnInit() {
    console.log('=== APP ngOnInit START ===');
    
    // Expose app instance for debugging (development only)
    if (typeof window !== 'undefined') {
      (window as any).appDebug = this;
      (window as any).screenshot = this.screenshotService;
      console.log('🔧 App component exposed to window.appDebug for debugging');
      console.log('🔧 Screenshot service exposed to window.screenshot');
      console.log('🔧 Call window.appDebug.debugAuthState() to check auth state');
      console.log('🔧 Call window.screenshot.enableScreenshotMode() for full-page screenshots');
      console.log('🔧 Call window.screenshot.preparePageForPrinting() for printing');
    }
    
    const fullUrl = window.location.href;
    const isOAuthCallback = fullUrl.includes('code=') || fullUrl.includes('state=');
    // Captured before initializeAuth() gets a chance to move anyone.
    const entryUrl = window.location.pathname.replace(/\/index\.html$/, '') + window.location.search;
    
    console.log('Full browser URL:', fullUrl);
    console.log('Is OAuth callback:', isOAuthCallback);
    
    // Initialize authentication FIRST, before setting up any subscriptions or routing
    console.log('🟡 About to call authService.initializeAuth()');
    const authResult = await this.authService.initializeAuth();
    console.log('🟡 authService.initializeAuth() returned:', authResult);
    
    if (authResult) {
      console.log('✅ Auth successful');
      this.startTokenMonitoring();
      
      // The session permission is derived from the user's actual roles once
      // GET /me/ resolves (see PermissionService.fetchUserRoles). We no longer
      // force a default 'admin' permission here.
      if (!this.permissionService.selectedPermission()) {
        const own = this.permissionService.defaultPermissionForRoles();
        if (own) this.permissionService.selectPermission(own);
      }
    } else {
      console.log('Auth failed - user needs to login');
      // Remember the page they were trying to open, before anything navigates
      // away from it. It cannot be left to authGuard: on a cold load
      // PermissionService's constructor sees "not authenticated" and calls
      // resetPermission(), whose navigate(['']) CANCELS the router's initial
      // navigation — so the guard never runs and never sees this URL. The
      // deep link died here, one step earlier than it appeared to.
      this.authService.rememberRoute(entryUrl);
    }
    
    // Now set up subscriptions AFTER auth is initialized
    // Subscribe to auth state changes to handle logout/expiration
    this.authService.isAuthenticated$.subscribe(isAuth => {
      console.log('🚨 Auth state changed from authService.isAuthenticated$:', isAuth);
      console.log('🚨 Current isAuthenticated computed value:', this.isAuthenticated());

      if (isAuth) {
        this.hasBeenAuthenticated = true;
        // Dismiss any session-expired snackbar when authentication succeeds
        if (this.expiredSnackBarRef) {
          console.log('🚨 Auth succeeded - dismissing expired session snackbar');
          this.expiredSnackBarRef.dismiss();
          this.expiredSnackBarRef = undefined;
        }
      } else {
        console.log('🚨 Auth state became false - checking if tokens actually exist');

        // Don't show expiration dialog if we still have valid tokens
        // This prevents false positives during cross-tab sync
        const hasToken = this.authService.getAccessToken();
        const tokenExpiry = this.authService.getTokenExpiry();
        const isActuallyExpired = tokenExpiry ? Date.now() >= tokenExpiry : true;

        console.log('🚨 Token check:', { hasToken: !!hasToken, tokenExpiry, isActuallyExpired });

        if (!hasToken || isActuallyExpired) {
          this.stopTokenMonitoring();
          if (this.hasBeenAuthenticated) {
            console.log('🚨 Session expired after prior auth — showing expired session snackbar');
            this.handleTokenExpiration();
          } else {
            console.log('🚨 No prior auth in this session — skipping expired snackbar (fresh load)');
          }
        } else {
          console.log('🚨 Auth state false but tokens are valid - likely cross-tab sync issue');
        }
      }
    });
    
    // Subscribe to router events to track current route
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      console.log('🔄 Route changed to:', event.url);
      this.currentRoute.set(event.url);
    });
    
    // Set initial route
    this.currentRoute.set(this.router.url);
    
    // Decide where this session lands. Only the cases where the URL names no
    // page of its own get a decision: an OAuth callback, the bare root, and the
    // Impersonate picker — which is opt-in from the profile menu, never
    // somewhere a session STARTS. A deep link the user actually typed is left
    // alone; the router is already taking them there.
    //
    // LandingService owns what "landing" means. See its comment for why this is
    // not four separate rules any more.
    if (authResult) {
      const path = (window.location.pathname || '/').replace(/\/index\.html$/, '');
      const onImpersonatePicker = /^\/?(impersonate|auth)\b/.test(path);
      if (isOAuthCallback || onImpersonatePicker || path === '' || path === '/') {
        await this.landingService.land();
      } else {
        // A URL matching no route — a stale bookmark such as the retired
        // /officer-search — is sent to '' by the catch-all, which is the root
        // page. For a signed-in user that page is a dead end: a "Go to
        // Dashboard" card costing an extra click. Land them like any other
        // root arrival.
        //
        // The check above cannot cover this: the catch-all resolves AFTER
        // ngOnInit, so window.location.pathname is still the unknown URL here.
        // Hence the wait for the first completed navigation. One-shot, so
        // deliberately visiting '/' later in the session is left alone.
        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          take(1),
        ).subscribe(() => {
          if (this.router.url === '/') {
            console.log('Unmatched URL was:', path, '— landing the session');
            this.landingService.land();
          }
        });
      }
    }

    console.log('=== APP ngOnInit END ===');
  }

  ngOnDestroy() {
    this.stopTokenMonitoring();
    // Owned by the auth state rather than by monitoring, so dismissed here.
    this.expiredSnackBarRef?.dismiss();
    this.expiredSnackBarRef = undefined;
    this.routerSubscription?.unsubscribe();
  }

  login() {
    console.log('=== APP login() clicked ===');
    this.authService.login();
  }

  goToDashboard() {
    console.log('=== APP goToDashboard() clicked ===');
    const permission = this.permissionService.selectedPermission();
    const isAuth = this.isAuthenticated();
    console.log('Current permission:', permission);
    console.log('Is authenticated:', isAuth);
    console.log('Available permissions:', this.permissionService.availablePermissions);
    
    // Ensure we have a permission set — derived from the user's own roles.
    if (!permission && isAuth) {
      const own = this.permissionService.defaultPermissionForRoles();
      if (own) this.permissionService.selectPermission(own);
    }
    
    // homeRoute() answers for both branches — an officer session belongs on
    // their own page, not on /dashboard, which their roles cannot open.
    this.router.navigateByUrl(this.permissionService.homeRoute());
  }

  // Add this method for debugging from browser console
  debugAuthState() {
    console.log('🔧 DEBUG AUTH STATE:');
    console.log('🔧 isAuthenticated signal:', this.isAuthenticated());
    console.log('🔧 showLoginPage computed:', this.showLoginPage());
    console.log('🔧 showDashboardButton computed:', this.showDashboardButton());
    console.log('🔧 currentRoute signal:', this.currentRoute());
    console.log('🔧 router.url:', this.router.url);
    console.log('🔧 Auth service hasValidAccessToken:', this.authService.isAuthenticated());
  }

  private startTokenMonitoring() {
    // Get the token expiration time in milliseconds
    const tokenExpiry = this.authService.getTokenExpiry();
    const nowInMs = Date.now();
    
    if (!tokenExpiry) {
      console.log('No token expiry found, skipping monitoring');
      return;
    }
    
    console.log('Token expiry timestamp:', tokenExpiry);
    console.log('Current timestamp:', nowInMs);
    console.log('Token expiry date:', new Date(tokenExpiry));
    console.log('Current date:', new Date(nowInMs));
    
    const totalMsUntilExpiry = tokenExpiry - nowInMs;
    console.log('Token expires in', Math.floor(totalMsUntilExpiry / 1000), 'seconds');
    
    if (totalMsUntilExpiry <= 0) {
      console.log('Token already expired');
      this.handleExpiredToken();
      return;
    }
    
    // Show warning 5 minutes before expiry
    const fiveMinutesInMs = 5 * 60 * 1000;
    if (totalMsUntilExpiry > fiveMinutesInMs) {
      const timeUntilWarning = totalMsUntilExpiry - fiveMinutesInMs;
      console.log('Setting warning timer for', Math.floor(timeUntilWarning / 1000), 'seconds');

      this.warningTimeoutId = window.setTimeout(() => {
        if (this.authService.isAuthenticated() && !this.warningSnackBarRef) {
          this.showExpirationWarning(5);
        }
      }, timeUntilWarning);
    } else {
      // Token expires within 5 minutes, show warning immediately
      const minutesLeft = Math.floor(totalMsUntilExpiry / 60000);
      this.showExpirationWarning(minutesLeft);
    }
    
    // Set timer for actual expiry
    console.log('Setting expiry timer for', Math.floor(totalMsUntilExpiry / 1000), 'seconds');
    
    this.expiryTimeoutId = window.setTimeout(() => {
      if (this.authService.isAuthenticated()) {
        console.log('Token expired, handling logout');
        this.handleExpiredToken();
      }
    }, totalMsUntilExpiry);
  }

  private stopTokenMonitoring() {
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = undefined;
    }
    
    if (this.expiryTimeoutId) {
      clearTimeout(this.expiryTimeoutId);
      this.expiryTimeoutId = undefined;
    }
    
    if (this.warningSnackBarRef) {
      this.warningSnackBarRef.dismiss();
      this.warningSnackBarRef = undefined;
    }

    // The expired-session snackbar is deliberately NOT cleared here. Its
    // lifecycle belongs to the auth state — opened when authentication drops,
    // dismissed when it returns — and this runs immediately before
    // handleTokenExpiration() on that same path. Clearing it here defeated that
    // method's "already showing" guard, so every further drop tore the snackbar
    // down and built a new one, making it flicker on a user who had not moved.
  }

  private showExpirationWarning(minutesLeft: number) {
    if (minutesLeft > 5) {
      return;
    }
    
    this.warningSnackBarRef = this.snackBar.open(
      `Your session expires in ${minutesLeft} minute(s).`,
      'Go to login',
      {
        duration: 0, // Persistent until dismissed
        panelClass: ['warning-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'top'
      }
    );

    this.warningSnackBarRef.onAction().subscribe(() => {
      this.login();
    });
  }

  private handleTokenExpiration() {
    // Only show if not already showing and user was previously authenticated
    if (this.expiredSnackBarRef) {
      return; // Already showing
    }
    
    // Clear permissions without redirecting
    this.permissionService.clearStateWithoutRedirect();

    // Show expired token notification without redirecting
    this.expiredSnackBarRef = this.snackBar.open(
      'Your session has expired. Please log in again to continue.',
      'Log In',
      {
        duration: 0, // Persistent until action taken
        panelClass: ['error-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'top'
      }
    );

    this.expiredSnackBarRef.onAction().subscribe(() => {
      this.expiredSnackBarRef?.dismiss();
      this.expiredSnackBarRef = undefined;
      this.login();
    });
  }

  private handleExpiredToken() {
    console.log('🔴 handleExpiredToken called');

    // Stop monitoring and clear any warning snackbar
    this.stopTokenMonitoring();

    // Remember where the user was so "Log In" (from the expired-session
    // snackbar) returns them here instead of dropping them on /dashboard.
    this.authService.rememberRoute(this.router.url);

    // Soft-clear auth state only — no CAS redirect, no navigation. The user
    // stays on their current page/form; the expired-session snackbar (shown
    // by the auth state subscription below) offers "Log In" to resume.
    console.log('🔴 Session expired — clearing auth state without redirecting');
    this.authService.handleSessionExpired();
  }
}
